import { InfoIcon } from '@phosphor-icons/react'
import { endOfDay } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import API from '@/api/api'
import { useUpdateClientKeylessSuspend } from '@/api/clients/update-client-keyless-suspend'
import { useConnectSaltoSpaceUser } from '@/api/keyless/salto-space/connect-salto-space-user'
import { useCreateSaltoSpaceUser } from '@/api/keyless/salto-space/create-salto-space-user'
import { useGetSaltoSpaceAccessPoints } from '@/api/keyless/salto-space/get-salto-space-access-points'
import { useGetSaltoSpaceDisconnectedUsers } from '@/api/keyless/salto-space/get-salto-space-disconnected-users'
import { useGetSaltoSpaceFobs } from '@/api/keyless/salto-space/get-salto-space-fobs'
import { useGetSaltoSpaceIsConnected } from '@/api/keyless/salto-space/get-salto-space-is-connected'
import { useGetSaltoSpaceUser } from '@/api/keyless/salto-space/get-salto-space-user'
import { useUpdateSaltoSpaceUser } from '@/api/keyless/salto-space/update-salto-space-user'
import { useIsSaltoSpaceBuildingConnected } from '@/api/property-keyless/salto-space/use-is-salto-space-building-connected'
import SaltoSpaceIssueFobModal from '@/components/Clients/ClientProfileView/IntegrationsSection/SaltoSpaceIssueFobModal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChipSelect } from '@/components/ui/chip-select'
import { Collapse } from '@/components/ui/collapse'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Flex } from '@/components/ui/flex'
import { Select } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { TextInput } from '@/components/ui/text-input'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { popupFactory, toastFactory } from '@/factories'
import { DirtyFieldChange } from '@/hooks'
import { formatDateOnly, parseLocalDate } from '@/utils/date'
import { getApiErrorMessage } from '@/utils/error'

import * as S from '../keyless-access.styled'
import { computeKeylessArrayDiff, computeKeylessDateDiff } from '../keyless-access.utils'
import { KeylessBuildingNotConnected } from '../keyless-access-building-not-connected'
import { KeylessNotConnected } from '../keyless-access-not-connected'
import { KeylessTabSkeleton } from '../keyless-access-tab-skeleton'
import { SaltoFobRecord, SaltoSpaceExternalFobs } from './salto-space-external-fobs'
import {
  getExpirationDateTooEarlyMessage,
  SALTO_INVALID_DATE_ORDER_MESSAGE,
} from './salto-space-tab.constants'
import { SaltoSpaceTabProps } from './salto-space-tab.types'

const parseAuthCodes = (input: string) =>
  input
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean)

export const SaltoSpaceTab = ({
  buildingId,
  clientId,
  clientEmail,
  clientName,
  clientApartment,
  isKeylessAccessSuspended,
  leaseStart,
  moveInDate,
  saltoSpaceFobsAvailable,
  isEditing,
  onEditingChange,
  onRegisterSave,
  onRegisterGetChanges,
  onLoadingChange,
  onConnectionChange,
}: SaltoSpaceTabProps) => {
  const { t } = useTranslation(['clients', 'common'])
  const [doorIds, setDoorIds] = useState<string[]>([])
  const [groupIds, setGroupIds] = useState<string[]>([])
  const [accessLevelIds, setAccessLevelIds] = useState<string[]>([])
  const [authCodesInput, setAuthCodesInput] = useState('')
  const [expirationDate, setExpirationDate] = useState<Date | null>(null)
  const [isSuspended, setIsSuspended] = useState(isKeylessAccessSuspended)
  const [selectedConnectUserId, setSelectedConnectUserId] = useState<string | null>(null)
  const [showDisconnectedList, setShowDisconnectedList] = useState(false)
  const [showFobModal, setShowFobModal] = useState(false)
  const [fobDoorIds, setFobDoorIds] = useState<string[]>([])
  const [fobGroupIds, setFobGroupIds] = useState<string[]>([])
  const [fobAccessLevelIds, setFobAccessLevelIds] = useState<string[]>([])
  const [fobExpirationDate, setFobExpirationDate] = useState<Date | null>(null)

  const {
    data: isConnectedRaw,
    isLoading,
    isSuccess: isConnectedSuccess,
    refetch: refetchConnected,
  } = useGetSaltoSpaceIsConnected({
    email: clientEmail,
  })
  // Note: gated on `isSuccess`, not just the raw response - React Query keeps the last
  // successful value in `data` even after a later background refetch fails, which would
  // otherwise leave this resident looking connected once a stale `true` was ever fetched.
  const isConnected = isConnectedSuccess && Boolean(isConnectedRaw)
  const { isBuildingConnected, isLoading: isLoadingBuildingConnection } =
    useIsSaltoSpaceBuildingConnected(buildingId)
  const {
    data: saltoUser,
    isFetching: isFetchingUser,
    refetch: refetchUser,
  } = useGetSaltoSpaceUser({ email: clientEmail }, Boolean(isConnected))
  const { data: accessPoints } = useGetSaltoSpaceAccessPoints(
    { buildingId },
    Boolean(isConnected) || saltoSpaceFobsAvailable,
  )
  const { data: fobs = [], refetch: refetchFobs } = useGetSaltoSpaceFobs(
    buildingId,
    clientEmail,
    Boolean(isConnected) && saltoSpaceFobsAvailable,
  )
  const internalFob = fobs.find((fob) => fob.fobMarker === 'internal' && fob.fobId) ?? null
  const {
    data: disconnectedUsers = [],
    isFetching: isFetchingDisconnected,
    refetch: refetchDisconnected,
  } = useGetSaltoSpaceDisconnectedUsers({ buildingId }, showDisconnectedList)

  const { mutateAsync: createUser, isPending: isCreating } = useCreateSaltoSpaceUser()
  const { mutateAsync: connectUser } = useConnectSaltoSpaceUser()
  const { mutateAsync: updateUser } = useUpdateSaltoSpaceUser()
  const { mutateAsync: updateSuspend } = useUpdateClientKeylessSuspend(clientId)

  useEffect(() => {
    if (!saltoUser) return
    setDoorIds(saltoUser.doorIds)
    setGroupIds(saltoUser.permissionGroupIds)
    setAccessLevelIds(saltoUser.accessLevelIds)
    setAuthCodesInput((saltoUser.authorizationCodes ?? []).join(', '))
    if (saltoUser.userExpiration) {
      setExpirationDate(parseLocalDate(saltoUser.userExpiration))
    }
  }, [saltoUser])

  const handleConfirmAccessRef = useRef<() => Promise<void>>(undefined)

  const handleOpenAccessConfirm = () =>
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.saltoSpace.approveAccess.confirmBody', {
        clientApartment: clientApartment ?? EMPTY_VALUE,
        clientName,
      }),
      confirmText: t('clients:keylessAccess.saltoSpace.approveAccess.confirmText'),
      onConfirm: () => handleConfirmAccessRef.current?.(),
      title: t('clients:keylessAccess.saltoSpace.approveAccess.confirmTitle'),
      variant: 'warning',
    })

  useEffect(() => {
    if (!isEditing) return
    onRegisterSave?.(handleOpenAccessConfirm)
    return () => onRegisterSave?.(null)
  }, [isEditing, onRegisterSave, clientName, clientApartment])

  const currentValuesRef = useRef({
    accessLevelIds,
    accessPoints,
    authCodesInput,
    doorIds,
    expirationDate,
    groupIds,
  })
  currentValuesRef.current = {
    accessLevelIds,
    accessPoints,
    authCodesInput,
    doorIds,
    expirationDate,
    groupIds,
  }
  const initialRef = useRef<{
    doorIds: string[]
    groupIds: string[]
    accessLevelIds: string[]
    authCodes: string[]
    expDate: Date | null
  }>({ accessLevelIds: [], authCodes: [], doorIds: [], expDate: null, groupIds: [] })

  useEffect(() => {
    if (!isEditing) {
      onRegisterGetChanges?.(null)
      return
    }
    const cur = currentValuesRef.current
    initialRef.current = {
      accessLevelIds: cur.accessLevelIds,
      authCodes: parseAuthCodes(cur.authCodesInput),
      doorIds: cur.doorIds,
      expDate: cur.expirationDate,
      groupIds: cur.groupIds,
    }
    onRegisterGetChanges?.(() => {
      const {
        accessLevelIds: curAccess,
        accessPoints: pts,
        authCodesInput: curAuthCodesInput,
        doorIds: curDoors,
        expirationDate: curDate,
        groupIds: curGroups,
      } = currentValuesRef.current
      const init = initialRef.current
      const changes: DirtyFieldChange[] = []
      const doorDiff = computeKeylessArrayDiff(
        init.doorIds,
        curDoors,
        'Door',
        (id) => pts?.doors.find((d) => d.saltoId === id)?.name ?? id,
      )
      if (doorDiff) changes.push(doorDiff)
      const zoneDiff = computeKeylessArrayDiff(
        init.groupIds,
        curGroups,
        'Zone',
        (id) => pts?.permissionGroups.find((g) => g.saltoId === id)?.name ?? id,
      )
      if (zoneDiff) changes.push(zoneDiff)
      const accessDiff = computeKeylessArrayDiff(
        init.accessLevelIds,
        curAccess,
        'Access Level',
        (id) => pts?.accessLevels.find((a) => a.saltoId === id)?.name ?? id,
      )
      if (accessDiff) changes.push(accessDiff)
      const authCodesDiff = computeKeylessArrayDiff(
        init.authCodes,
        parseAuthCodes(curAuthCodesInput),
        'Authorization Code',
        (code) => code,
      )
      if (authCodesDiff) changes.push(authCodesDiff)
      const dateDiff = computeKeylessDateDiff(init.expDate, curDate, 'Expiration Date')
      if (dateDiff) changes.push(dateDiff)
      return changes
    })
    return () => onRegisterGetChanges?.(null)
  }, [isEditing, onRegisterGetChanges])

  const isTabLoading = isLoading || isFetchingUser || isLoadingBuildingConnection

  useEffect(() => {
    onLoadingChange?.(isTabLoading)
    return () => onLoadingChange?.(false)
  }, [isTabLoading, onLoadingChange])

  useEffect(() => {
    if (isLoading || isLoadingBuildingConnection) return
    onConnectionChange?.(isBuildingConnected && Boolean(isConnected))
  }, [isConnected, isBuildingConnected, isLoading, isLoadingBuildingConnection, onConnectionChange])

  if (isTabLoading) return <KeylessTabSkeleton rowCount={4} />

  if (!isBuildingConnected) {
    return (
      <KeylessBuildingNotConnected
        title={t('clients:keylessAccess.saltoSpace.buildingNotConnected.title')}
        message={t('clients:keylessAccess.saltoSpace.buildingNotConnected.message')}
      />
    )
  }

  const allDoors = accessPoints?.doors ?? []
  const allGroups = accessPoints?.permissionGroups ?? []
  const allAccessLevels = accessPoints?.accessLevels ?? []

  const doorOptions = allDoors.map((d) => ({ label: d.name, value: d.saltoId }))
  const zoneOptions = allGroups.map((g) => ({ label: g.name, value: g.saltoId }))
  const accessLevelOptions = allAccessLevels.map((a) => ({ label: a.name, value: a.saltoId }))

  const selectedDoors = allDoors.filter((d) => doorIds.includes(d.saltoId))
  const selectedGroups = allGroups.filter((g) => groupIds.includes(g.saltoId))
  const selectedAccessLevels = allAccessLevels.filter((a) => accessLevelIds.includes(a.saltoId))

  const savedAuthCodes = saltoUser?.authorizationCodes ?? []
  const parsedAuthCodes = parseAuthCodes(authCodesInput)

  const handleCreateNewUser = async () => {
    try {
      await createUser({ buildingId, email: clientEmail })
      toastFactory.success(t('clients:keylessAccess.saltoSpace.toasts.createSuccess'))
      void refetchConnected()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleFetchConnectList = async () => {
    const result = await refetchDisconnected()
    const users = result.data ?? []
    if (users.length === 0) {
      toastFactory.error(t('clients:keylessAccess.saltoSpace.toasts.noAccountsToConnect'))
    } else {
      setShowDisconnectedList(true)
    }
  }

  const handleConnectConfirmed = async () => {
    if (!selectedConnectUserId) return
    try {
      await connectUser({ buildingId, email: clientEmail, saltoUserId: selectedConnectUserId })
      toastFactory.success(t('clients:keylessAccess.saltoSpace.toasts.connectSuccess'))
      setShowDisconnectedList(false)
      void refetchConnected()
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleConfirmAccess = async () => {
    try {
      await updateUser({
        accessLevelIds,
        authorizationCodes:
          parsedAuthCodes.join(',') !== savedAuthCodes.join(',') ? parsedAuthCodes : undefined,
        doorIds,
        email: clientEmail,
        expirationDate: expirationDate ? endOfDay(expirationDate).getTime() : undefined,
        permissionGroupIds: groupIds,
      })
      toastFactory.success(t('clients:keylessAccess.saltoSpace.toasts.accessGrantedSuccess'))
      void refetchUser()
      onEditingChange(false)
    } catch (error) {
      const message = getApiErrorMessage(error)
      toastFactory.error(
        message.includes(SALTO_INVALID_DATE_ORDER_MESSAGE)
          ? getExpirationDateTooEarlyMessage(leaseStart, moveInDate, t)
          : message,
      )
    }
  }
  handleConfirmAccessRef.current = handleConfirmAccess

  const handleConfirmSuspend = async (value: boolean) => {
    try {
      await updateSuspend({ clientId, isKeylessAccessSuspended: value })
      setIsSuspended(value)
      toastFactory.success(
        value
          ? t('clients:keylessAccess.saltoSpace.toasts.suspendSuccess')
          : t('clients:keylessAccess.saltoSpace.toasts.unsuspendSuccess'),
      )
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSuspendToggle = (value: boolean) => {
    popupFactory.open(ConfirmationPopup, {
      children: value
        ? t('clients:keylessAccess.saltoSpace.suspendConfirmation.suspend.body', { clientName })
        : t('clients:keylessAccess.saltoSpace.suspendConfirmation.unsuspend.body', { clientName }),
      confirmText: value
        ? t('clients:keylessAccess.saltoSpace.suspendConfirmation.suspend.confirmText')
        : t('clients:keylessAccess.saltoSpace.suspendConfirmation.unsuspend.confirmText'),
      onConfirm: () => handleConfirmSuspend(value),
      title: t('clients:keylessAccess.saltoSpace.suspendConfirmation.title'),
      variant: 'warning',
    })
  }

  const connectOptions = disconnectedUsers.map((u) => ({ label: u.name, value: u.saltoUserId }))

  const handleOpenCreateConfirm = () => {
    setShowDisconnectedList(false)
    setSelectedConnectUserId(null)
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.saltoSpace.createUser.confirmBody'),
      confirmText: t('clients:keylessAccess.saltoSpace.createUser.confirmText'),
      onConfirm: handleCreateNewUser,
      title: t('clients:keylessAccess.saltoSpace.createUser.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleOpenConnectConfirm = () => {
    popupFactory.open(ConfirmationPopup, {
      children: t('clients:keylessAccess.saltoSpace.connectUser.confirmBody'),
      confirmText: t('clients:keylessAccess.saltoSpace.connectUser.confirmText'),
      onConfirm: handleConnectConfirmed,
      title: t('clients:keylessAccess.saltoSpace.connectUser.confirmTitle'),
      variant: 'warning',
    })
  }

  const handleIssueFob = (encoderId: string, force?: boolean) =>
    API.issueSaltoSpaceFob(
      buildingId,
      encoderId,
      clientEmail,
      {
        accessLevelIds: fobAccessLevelIds,
        doorIds: fobDoorIds,
        expirationDate: fobExpirationDate ? endOfDay(fobExpirationDate).toISOString() : undefined,
        permissionGroupIds: fobGroupIds,
      },
      force,
    ).then(() => undefined)

  const handleIssueSuccess = () => {
    void refetchFobs()
    setFobDoorIds([])
    setFobGroupIds([])
    setFobAccessLevelIds([])
    setFobExpirationDate(null)
  }

  const isFobButtonEnabled =
    (fobDoorIds.length > 0 || fobGroupIds.length > 0 || fobAccessLevelIds.length > 0) &&
    Boolean(fobExpirationDate)

  const fobBlock = saltoSpaceFobsAvailable ? (
    <S.FobSection>
      <S.FobSectionTitle>
        <Typography size="text-medium" weight="medium" color="black800" as="span">
          {t('clients:keylessAccess.saltoSpace.fob.sectionTitle')}
        </Typography>
      </S.FobSectionTitle>
      {internalFob ? (
        <SaltoFobRecord
          fob={internalFob}
          buildingId={buildingId}
          clientEmail={clientEmail}
          doorOptions={doorOptions}
          zoneOptions={zoneOptions}
          accessLevelOptions={accessLevelOptions}
        />
      ) : (
        <>
          <S.TabContent>
            <S.FieldRow>
              <Flex gap={6} align="center">
                <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                  {t('clients:keylessAccess.saltoSpace.door.label')}
                </Typography>
              </Flex>
              <ChipSelect
                size="small"
                options={doorOptions}
                value={fobDoorIds}
                placeholder={t('clients:keylessAccess.saltoSpace.door.placeholder')}
                onChange={setFobDoorIds}
              />
            </S.FieldRow>
            <S.FieldRow>
              <Flex gap={6} align="center">
                <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                  {t('clients:keylessAccess.saltoSpace.zone.label')}
                </Typography>
              </Flex>
              <ChipSelect
                size="small"
                options={zoneOptions}
                value={fobGroupIds}
                placeholder={t('clients:keylessAccess.saltoSpace.zone.placeholder')}
                onChange={setFobGroupIds}
              />
            </S.FieldRow>
            <S.FieldRow>
              <Flex gap={6} align="center">
                <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                  {t('clients:keylessAccess.saltoSpace.accessLevel.label')}
                </Typography>
              </Flex>
              <ChipSelect
                size="small"
                options={accessLevelOptions}
                value={fobAccessLevelIds}
                placeholder={t('clients:keylessAccess.saltoSpace.accessLevel.placeholder')}
                onChange={setFobAccessLevelIds}
              />
            </S.FieldRow>
            <S.FieldRow>
              <Flex gap={6} align="center">
                <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                  {t('clients:keylessAccess.saltoSpace.expirationDate.label')}
                </Typography>
              </Flex>
              <DatePickerInput
                size="small"
                placeholder={t('clients:keylessAccess.saltoSpace.expirationDate.placeholder')}
                value={fobExpirationDate}
                minDate={new Date()}
                onChange={setFobExpirationDate}
                onRestore={() => setFobExpirationDate(null)}
              />
            </S.FieldRow>
          </S.TabContent>
          <Flex justify="flex-end">
            <Button
              size="small"
              disabled={!isFobButtonEnabled}
              onClick={() => setShowFobModal(true)}
            >
              {t('clients:keylessAccess.saltoSpace.fob.issueButton')}
            </Button>
          </Flex>
        </>
      )}
      {showFobModal && (
        <SaltoSpaceIssueFobModal
          allowOverride
          isOpen={showFobModal}
          buildingId={buildingId}
          onProgram={handleIssueFob}
          onClose={() => setShowFobModal(false)}
          onSuccess={handleIssueSuccess}
        />
      )}
    </S.FobSection>
  ) : null

  const externalFobsBlock = (
    <SaltoSpaceExternalFobs
      buildingId={buildingId}
      clientEmail={clientEmail}
      enabled={Boolean(isConnected) && saltoSpaceFobsAvailable}
      doorOptions={doorOptions}
      zoneOptions={zoneOptions}
      accessLevelOptions={accessLevelOptions}
    />
  )

  if (!isConnected) {
    return (
      <>
        <S.TabContent>
          <KeylessNotConnected
            label={t('clients:keylessAccess.saltoSpace.label')}
            isCreating={isCreating}
            isConnecting={isFetchingDisconnected}
            isConnectDisabled={showDisconnectedList}
            onCreateNew={handleOpenCreateConfirm}
            onConnectExisting={() => void handleFetchConnectList()}
          />
          <Collapse in={showDisconnectedList} animation="grow">
            <S.FieldRow>
              <Flex gap={6} align="center">
                <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                  {t('clients:keylessAccess.saltoSpace.selectUser.label')}
                </Typography>
              </Flex>
              <Flex gap={12} justify="flex-end" align="center">
                <Select
                  size="small"
                  options={connectOptions}
                  value={selectedConnectUserId}
                  onChange={(value) => setSelectedConnectUserId(value as string)}
                  onReset={() => setSelectedConnectUserId(null)}
                />
                <Button
                  size="small"
                  disabled={!selectedConnectUserId}
                  onClick={handleOpenConnectConfirm}
                >
                  {t('common:actions.confirm')}
                </Button>
              </Flex>
            </S.FieldRow>
          </Collapse>
        </S.TabContent>
        {fobBlock}
      </>
    )
  }

  const suspendRow = (
    <Flex align="center" gap={8}>
      <Switch checked={isSuspended} onChange={(e) => handleSuspendToggle(e.target.checked)} />
      <Typography size="text-small" weight="medium" color="black800" as="span" whiteSpace="nowrap">
        {t('clients:keylessAccess.saltoSpace.suspendToggle')}
      </Typography>
      <Tooltip triggerIcon={InfoIcon}>
        {t('clients:keylessAccess.saltoSpace.suspendToggleTooltip')}
      </Tooltip>
    </Flex>
  )

  if (!isEditing) {
    return (
      <>
        <S.TabContent>
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.saltoSpace.door.label')}
              </Typography>
            </Flex>
            <S.FieldValue>
              {selectedDoors.length > 0 ? (
                <Flex gap={6} wrap="wrap">
                  {selectedDoors.map((d) => (
                    <Badge key={d.saltoId} color="blue" size="small" variant="light">
                      {d.name}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                EMPTY_VALUE
              )}
            </S.FieldValue>
          </S.FieldRow>
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.saltoSpace.zone.label')}
              </Typography>
            </Flex>
            <S.FieldValue>
              {selectedGroups.length > 0 ? (
                <Flex gap={6} wrap="wrap">
                  {selectedGroups.map((g) => (
                    <Badge key={g.saltoId} color="blue" size="small" variant="light">
                      {g.name}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                EMPTY_VALUE
              )}
            </S.FieldValue>
          </S.FieldRow>
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.saltoSpace.accessLevel.label')}
              </Typography>
            </Flex>
            <S.FieldValue>
              {selectedAccessLevels.length > 0 ? (
                <Flex gap={6} wrap="wrap">
                  {selectedAccessLevels.map((a) => (
                    <Badge key={a.saltoId} color="blue" size="small" variant="light">
                      {a.name}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                EMPTY_VALUE
              )}
            </S.FieldValue>
          </S.FieldRow>
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.saltoSpace.authorizationCodes.label')}
              </Typography>
            </Flex>
            <S.FieldValue>
              {savedAuthCodes.length > 0 ? (
                <Flex gap={6} wrap="wrap">
                  {savedAuthCodes.map((code, index) => (
                    <Badge key={`${code}-${index}`} color="blue" size="small" variant="light">
                      {code}
                    </Badge>
                  ))}
                </Flex>
              ) : (
                EMPTY_VALUE
              )}
            </S.FieldValue>
          </S.FieldRow>
          <S.FieldRow>
            <Flex gap={6} align="center">
              <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
                {t('clients:keylessAccess.saltoSpace.expirationDate.label')}
              </Typography>
            </Flex>
            <S.FieldValue>
              {expirationDate ? formatDateOnly(expirationDate) : EMPTY_VALUE}
            </S.FieldValue>
          </S.FieldRow>
        </S.TabContent>
        {suspendRow}
        {fobBlock}
        {externalFobsBlock}
      </>
    )
  }

  return (
    <>
      <S.TabContent>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.saltoSpace.door.label')}
            </Typography>
          </Flex>
          <ChipSelect
            size="small"
            options={doorOptions}
            value={doorIds}
            placeholder={t('clients:keylessAccess.saltoSpace.door.placeholder')}
            onChange={setDoorIds}
          />
        </S.FieldRow>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.saltoSpace.zone.label')}
            </Typography>
          </Flex>
          <ChipSelect
            size="small"
            options={zoneOptions}
            value={groupIds}
            placeholder={t('clients:keylessAccess.saltoSpace.zone.placeholder')}
            onChange={setGroupIds}
          />
        </S.FieldRow>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.saltoSpace.accessLevel.label')}
            </Typography>
          </Flex>
          <ChipSelect
            size="small"
            options={accessLevelOptions}
            value={accessLevelIds}
            placeholder={t('clients:keylessAccess.saltoSpace.accessLevel.placeholder')}
            onChange={setAccessLevelIds}
          />
        </S.FieldRow>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.saltoSpace.authorizationCodes.label')}
            </Typography>
          </Flex>
          <TextInput
            size="small"
            characterLimit={255}
            placeholder={t('clients:keylessAccess.saltoSpace.authorizationCodes.placeholder')}
            value={authCodesInput}
            onChange={(e) => setAuthCodesInput(e.target.value)}
          />
        </S.FieldRow>
        <S.FieldRow>
          <Flex gap={6} align="center">
            <Typography size="text-medium" color="primary500" as="span" whiteSpace="nowrap">
              {t('clients:keylessAccess.saltoSpace.expirationDate.label')}
            </Typography>
          </Flex>
          <DatePickerInput
            size="small"
            placeholder={t('clients:keylessAccess.saltoSpace.expirationDate.placeholder')}
            value={expirationDate}
            minDate={new Date()}
            onChange={setExpirationDate}
            onRestore={() => setExpirationDate(null)}
          />
        </S.FieldRow>
      </S.TabContent>
      {suspendRow}
      {fobBlock}
      {externalFobsBlock}
    </>
  )
}
